import { detectDistanceLocale } from '@wineryxplorer/shared';
import { trpc } from '../lib/trpc';
import { useAuth } from './useAuth';

import type { DistanceUnit } from '@wineryxplorer/shared';

/**
 * The browser's best guess at the viewer's unit.
 *
 * Browsers expose no measurement-system setting the way iOS does, so this reads
 * the locale's REGION rather than its language — an American browsing in German
 * (`de-US`) still gets miles, which matching on the language prefix got wrong.
 * Falls back to metric when the region cannot be determined, since most of the
 * world is metric.
 */
const browserDistanceUnit = (): DistanceUnit => {
  if (typeof navigator === 'undefined') return 'km';
  const tag = navigator.language;
  if (!tag) return 'km';
  try {
    const region = new Intl.Locale(tag).maximize().region;
    if (!region) return 'km';
    return detectDistanceLocale(region) === 'imperial' ? 'mi' : 'km';
  } catch {
    // Malformed language tag — not worth failing a render over.
    return 'km';
  }
};

/**
 * The viewer's distance unit: their explicit choice when they have made one,
 * otherwise the browser locale's region default.
 *
 * `user.getMe.distanceUnit` stays undefined until the user picks a unit in
 * Profile > Settings — the field has no schema default precisely so that
 * "never chose" remains distinguishable from "chose miles". Anything that
 * renders a distance should call this rather than hardcoding a unit.
 */
export const useDistanceUnit = (): DistanceUnit => {
  const { user } = useAuth();
  const me = trpc.user.getMe.useQuery(undefined, {
    enabled: Boolean(user),
    staleTime: 5 * 60 * 1000,
  });
  if (me.data?.distanceUnit === 'mi' || me.data?.distanceUnit === 'km') {
    return me.data.distanceUnit;
  }
  return browserDistanceUnit();
};
